import { useState, useEffect, useCallback, useMemo } from 'react';
import { getCurrentDeposits, getPastDeposits, returnDeposit, getLandlordDeposits, updateLandlordDeposit, updateTenant, getTenants } from '@/lib/api';
import { getBankTransactions, getTransactionCategories, linkTransactionToTenant } from '@/lib/bankTransactionsApi';
import { TenantDetailModal } from '@/components/TenantDetailModal';
import { ReturnDepositDialog } from '@/components/deposits/ReturnDepositDialog';
import { EditDepositDialog } from '@/components/deposits/EditDepositDialog';
import TransactionEditDialog from '@/components/TransactionEditDialog';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ChevronDown, ChevronRight, Pencil, Check, X, ArrowDownLeft, ArrowUpCircle, ArrowDownCircle, Link2, Unlink, Search, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const fmtMoney = (v) => v != null && v !== '' ? `$${parseFloat(v).toLocaleString()}` : '-';
const fmtDate = (v) => {
  if (!v) return '-';
  try {
    const d = new Date(v + 'T00:00:00');
    return `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}/${String(d.getFullYear()).slice(-2)}`;
  } catch { return v; }
};
const getQuarterLabel = (dateStr) => {
  if (!dateStr) return 'Unknown';
  const d = new Date(dateStr + 'T00:00:00');
  const quarters = ['Q1 (Jan-Mar)', 'Q2 (Apr-Jun)', 'Q3 (Jul-Sep)', 'Q4 (Oct-Dec)'];
  return `${quarters[Math.floor(d.getMonth() / 3)]} ${d.getFullYear()}`;
};
const getQuarterKey = (dateStr) => {
  if (!dateStr) return '0000-0';
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.getFullYear()}-${Math.floor(d.getMonth() / 3)}`;
};
const getTenantStatus = (tenant) => {
  const today = new Date().toISOString().split('T')[0];
  if (tenant.move_in_date > today) return 'future';
  if (tenant.move_out_date < today) return 'past';
  return 'current';
};

export default function DepositsPage() {
  const [tab, setTab] = useState('current');
  const [currentData, setCurrentData] = useState({ deposits: [], total: 0 });
  const [pastData, setPastData] = useState({ deposits: [] });
  const [landlordData, setLandlordData] = useState({ properties: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const [returnDialog, setReturnDialog] = useState(null);
  const [returnForm, setReturnForm] = useState({ return_date: '', return_method: '', return_amount: '', return_notes: '' });
  const [confirmReturn, setConfirmReturn] = useState(false);
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editingLandlord, setEditingLandlord] = useState(null);
  const [landlordAmount, setLandlordAmount] = useState('');
  const [expandedQuarters, setExpandedQuarters] = useState({});
  const [expandedLandlordProps, setExpandedLandlordProps] = useState({});
  const [tenantDetailId, setTenantDetailId] = useState(null);
  const [saving, setSaving] = useState(false);

  // P&L deposit transactions (security deposit category) + reconciliation
  const [pnlTxns, setPnlTxns] = useState([]);
  const [pnlCategories, setPnlCategories] = useState([]);
  const [pnlEditOpen, setPnlEditOpen] = useState(false);
  const [pnlEditIndex, setPnlEditIndex] = useState(-1);
  const pnlEditTarget = pnlEditIndex >= 0 && pnlEditIndex < pnlTxns.length ? pnlTxns[pnlEditIndex] : null;

  // All tenants (for manual linking) + link dialog state
  const [allTenants, setAllTenants] = useState([]);
  const [linkDialogTxn, setLinkDialogTxn] = useState(null);  // txn being linked
  const [linkSearch, setLinkSearch] = useState('');
  const [linkSaving, setLinkSaving] = useState(false);

  const fetchPnlDeposits = useCallback(async () => {
    try {
      const [all, cats, tenants] = await Promise.all([
        getBankTransactions({}),
        getTransactionCategories(),
        getTenants(),
      ]);
      const deposits = all.filter(t => {
        const cat = (t.category || '').toLowerCase();
        const sub = (t.subcategory || '').toLowerCase();
        return cat.includes('security deposit') || sub.includes('security deposit');
      });
      deposits.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      setPnlTxns(deposits);
      setPnlCategories(cats);
      setAllTenants(tenants);
    } catch {
      toast.error('Failed to load P&L deposits');
    }
  }, []);

  useEffect(() => { fetchPnlDeposits(); }, [fetchPnlDeposits]);

  const pnlTotal = useMemo(
    () => pnlTxns.reduce((s, t) => s + (t.amount || 0), 0),
    [pnlTxns]
  );

  // ─── Reconciliation: manual link first, then auto-match by amount + date ±14d ───
  const reconciliation = useMemo(() => {
    const DAYS_TOL = 14;
    const daysBetween = (a, b) => {
      if (!a || !b) return Infinity;
      const d1 = new Date(a + 'T00:00:00');
      const d2 = new Date(b + 'T00:00:00');
      return Math.abs((d1 - d2) / (1000 * 60 * 60 * 24));
    };
    const allDeposits = [
      ...(currentData.deposits || []),
      ...(pastData.deposits || []),
    ];
    const depositByTenantId = {};
    for (const d of allDeposits) depositByTenantId[d.id] = d;

    const map = {};
    for (const txn of pnlTxns) {
      const sub = (txn.subcategory || '').toLowerCase();
      const cat = (txn.category || '').toLowerCase();
      const isReturn = sub.includes('returned') || cat.includes('returned') || txn.type === 'expense' || (txn.amount || 0) < 0;
      const amount = Math.abs(txn.amount || 0);

      // Manual link wins
      if (txn.linked_tenant_id) {
        const manual = depositByTenantId[txn.linked_tenant_id];
        if (manual) {
          map[txn.id] = { deposit: manual, days: 0, matchType: isReturn ? 'return' : 'received', manual: true };
          continue;
        }
        // Fallback: find tenant in allTenants list (in case they aren't a current/past-with-deposit record)
        const t = allTenants.find(x => x.id === txn.linked_tenant_id);
        if (t) {
          map[txn.id] = {
            deposit: { id: t.id, name: t.name || `${t.first_name || ''} ${t.last_name || ''}`.trim(), unit_number: t.unit_number, property_name: t.property_name },
            days: 0,
            matchType: isReturn ? 'return' : 'received',
            manual: true,
          };
          continue;
        }
      }

      // If the user explicitly rejected auto-match for this txn, leave it unmatched
      if (txn.manual_unlink) {
        map[txn.id] = null;
        continue;
      }

      // Auto-match fallback
      let best = null;
      for (const dep of allDeposits) {
        const depAmt = isReturn ? (dep.deposit_return_amount || 0) : (dep.deposit_amount || 0);
        const depDate = isReturn ? dep.deposit_return_date : dep.deposit_date;
        if (!depAmt || !depDate) continue;
        const amtDiff = Math.abs(depAmt - amount);
        const days = daysBetween(depDate, txn.date);
        if (amtDiff <= 1 && days <= DAYS_TOL) {
          if (!best || days < best.days) {
            best = { deposit: dep, days, matchType: isReturn ? 'return' : 'received', manual: false };
          }
        }
      }
      map[txn.id] = best;
    }
    return map;
  }, [pnlTxns, currentData, pastData, allTenants]);

  // Tenant ids that have at least one bank transaction reconciled to them
  const reconciledTenantIds = useMemo(() => {
    const s = new Set();
    for (const v of Object.values(reconciliation)) {
      if (v && v.deposit?.id) s.add(v.deposit.id);
    }
    return s;
  }, [reconciliation]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [curr, past, landlord] = await Promise.all([getCurrentDeposits(), getPastDeposits(), getLandlordDeposits()]);
      setCurrentData(curr); setPastData(past); setLandlordData(landlord);
    } catch { toast.error('Failed to load deposits data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── Manual link: link/unlink/reject-auto a P&L txn ───
  const handleLinkTenant = async (txn, tenantId, rejectAutoMatch = false) => {
    setLinkSaving(true);
    try {
      const updated = await linkTransactionToTenant(txn.id, tenantId, rejectAutoMatch);
      // Patch local txn list in place
      setPnlTxns(list => list.map(t => t.id === txn.id ? {
        ...t,
        linked_tenant_id: updated.linked_tenant_id || null,
        manual_unlink: !!updated.manual_unlink,
      } : t));
      if (tenantId) toast.success('Linked to tenant');
      else if (rejectAutoMatch) toast.success('Marked as not this tenant');
      else toast.success('Unlinked');
      setLinkDialogTxn(null);
      setLinkSearch('');
    } catch { toast.error('Failed to update link'); }
    finally { setLinkSaving(false); }
  };

  // How many P&L txns are currently linked to each tenant id
  const linkedCountByTenant = useMemo(() => {
    const m = {};
    for (const t of pnlTxns) if (t.linked_tenant_id) m[t.linked_tenant_id] = (m[t.linked_tenant_id] || 0) + 1;
    return m;
  }, [pnlTxns]);

  const openReturnDialog = (deposit) => {
    setReturnDialog(deposit);
    setReturnForm({ return_date: new Date().toISOString().split('T')[0], return_method: '', return_amount: deposit.deposit_amount || '', return_notes: '' });
    setConfirmReturn(false);
  };

  const handleReturnDeposit = async () => {
    if (!confirmReturn) { setConfirmReturn(true); return; }
    setSaving(true);
    try {
      await returnDeposit(returnDialog.id, {
        return_date: returnForm.return_date,
        return_method: returnForm.return_method,
        return_amount: returnForm.return_amount ? parseFloat(returnForm.return_amount) : null,
        return_notes: returnForm.return_notes || ''
      });
      toast.success('Deposit returned successfully');
      setReturnDialog(null);
      fetchData();
    } catch { toast.error('Failed to return deposit'); }
    finally { setSaving(false); }
  };

  const openEditDialog = (deposit) => {    setEditDialog(deposit);
    setEditForm({
      deposit_amount: deposit.deposit_amount || '', deposit_date: deposit.deposit_date || '',
      payment_method: deposit.payment_method || '', notes: deposit.notes || '',
      deposit_return_date: deposit.deposit_return_date || '',
      deposit_return_amount: deposit.deposit_return_amount || '',
      deposit_return_method: deposit.deposit_return_method || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editDialog) return;
    setSaving(true);
    try {
      const updates = { ...editDialog };
      if (editForm.deposit_amount !== '') updates.deposit_amount = parseFloat(editForm.deposit_amount);
      updates.deposit_date = editForm.deposit_date;
      updates.payment_method = editForm.payment_method;
      updates.notes = editForm.notes;
      if (editForm.deposit_return_date) updates.deposit_return_date = editForm.deposit_return_date;
      if (editForm.deposit_return_amount !== '') updates.deposit_return_amount = parseFloat(editForm.deposit_return_amount);
      if (editForm.deposit_return_method) updates.deposit_return_method = editForm.deposit_return_method;
      await updateTenant(editDialog.id, updates);
      toast.success('Deposit info updated');
      setEditDialog(null);
      fetchData();
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const handleSaveLandlordDeposit = async (unitId) => {
    try {
      await updateLandlordDeposit(unitId, parseFloat(landlordAmount) || 0);
      toast.success('Landlord deposit updated');
      setEditingLandlord(null);
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const thClass = "px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap border-b border-border/60";
  const tdClass = "px-3 py-2.5 text-[12px] whitespace-nowrap border-b border-border/40";

  const StatusBadge = ({ tenant }) => {
    const status = getTenantStatus(tenant);
    if (status === 'past') return <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50 ml-1">Past Tenant</Badge>;
    if (status === 'future') return <Badge variant="outline" className="text-[10px] border-sky-300 text-sky-700 bg-sky-50 ml-1">Future Tenant</Badge>;
    return null;
  };

  const UnlinkedBadge = ({ tenant, forReturn = false }) => {
    // Only show if there's actually a deposit amount on the relevant side
    const amt = forReturn ? tenant.deposit_return_amount : tenant.deposit_amount;
    if (!amt) return null;
    if (reconciledTenantIds.has(tenant.id)) return null;
    return (
      <Badge
        variant="outline"
        className="text-[10px] border-amber-300 text-amber-700 bg-amber-50 ml-1 gap-1"
        title="No bank transaction reconciled to this deposit"
        data-testid="unlinked-deposit-badge"
      >
        <AlertCircle className="h-2.5 w-2.5" />Unlinked
      </Badge>
    );
  };

  const renderCurrentTab = () => (
    <div>
      <Card className="mb-4 bg-emerald-50 border-emerald-200">
        <CardContent className="py-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Total Deposits Held</p>
          <p className="font-heading text-3xl font-bold tabular-nums text-emerald-900" data-testid="deposits-total">{fmtMoney(currentData.total)}</p>
        </CardContent>
      </Card>
      {currentData.deposits.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">No current deposits</div>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-muted/50"><tr>
                <th className={thClass}>Tenant</th><th className={thClass}>Property / Unit</th>
                <th className={thClass}>Move In</th><th className={thClass}>Move Out</th>
                <th className={thClass}>Deposit Amount</th><th className={thClass}>Deposit Method</th>
                <th className={thClass}>Deposit Date</th><th className={thClass}>Notes</th>
                <th className={thClass}>Actions</th>
              </tr></thead>
              <tbody>
                {currentData.deposits.map((d, idx) => (
                  <tr key={d.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-muted/20'} data-testid="deposit-row">
                    <td className={tdClass}><button className="font-medium text-blue-600 hover:underline" onClick={() => setTenantDetailId(d.id)}>{d.name}</button><StatusBadge tenant={d} /><UnlinkedBadge tenant={d} /></td>
                    <td className={tdClass}>{d.property_name} / Unit {d.unit_number}</td>
                    <td className={`${tdClass} tabular-nums`}>{fmtDate(d.move_in_date)}</td>
                    <td className={`${tdClass} tabular-nums`}>{fmtDate(d.move_out_date)}</td>
                    <td className={`${tdClass} tabular-nums font-medium`}>{fmtMoney(d.deposit_amount)}</td>
                    <td className={tdClass}>{d.payment_method || '-'}</td>
                    <td className={`${tdClass} tabular-nums`}>{fmtDate(d.deposit_date)}</td>
                    <td className={`${tdClass} max-w-[150px] truncate`} title={d.notes || ''}>{d.notes || '-'}</td>
                    <td className={tdClass}>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEditDialog(d)} data-testid="edit-deposit-btn"><Pencil className="h-3 w-3" /></Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => openReturnDialog(d)} data-testid="return-deposit-btn"><ArrowDownLeft className="h-3 w-3" /> Return</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );

  const renderPastTab = () => {
    const deposits = pastData.deposits || [];
    if (deposits.length === 0) return <div className="text-center py-12 text-muted-foreground text-sm">No past deposits</div>;
    const quarterGroups = {};
    deposits.forEach(d => {
      const qKey = getQuarterKey(d.deposit_return_date);
      const qLabel = getQuarterLabel(d.deposit_return_date);
      if (!quarterGroups[qKey]) quarterGroups[qKey] = { label: qLabel, deposits: [] };
      quarterGroups[qKey].deposits.push(d);
    });
    const sorted = Object.entries(quarterGroups).sort((a, b) => b[0].localeCompare(a[0]));
    return (
      <div className="space-y-2">
        {sorted.map(([qKey, { label, deposits: qDeposits }]) => {
          const isExpanded = expandedQuarters[qKey];
          return (
            <Card key={qKey} className="overflow-hidden">
              <button className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
                onClick={() => setExpandedQuarters(p => ({ ...p, [qKey]: !p[qKey] }))}>
                <div className="flex items-center gap-2">
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <span className="text-sm font-semibold">{label}</span>
                  <Badge variant="secondary" className="text-xs">{qDeposits.length}</Badge>
                </div>
              </button>
              {isExpanded && (
                <div className="overflow-x-auto border-t">
                  <table className="w-full border-collapse">
                    <thead className="bg-muted/40"><tr>
                      <th className={thClass}>Tenant</th><th className={thClass}>Property / Unit</th>
                      <th className={thClass}>Move In</th><th className={thClass}>Move Out</th>
                      <th className={thClass}>Deposit Amt</th><th className={thClass}>Return Date</th>
                      <th className={thClass}>Return Amt</th><th className={thClass}>Return Method</th>
                      <th className={thClass}>Actions</th>
                    </tr></thead>
                    <tbody>
                      {qDeposits.map((d, idx) => (
                        <tr key={d.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-muted/20'}>
                          <td className={tdClass}><button className="font-medium text-blue-600 hover:underline" onClick={() => setTenantDetailId(d.id)}>{d.name}</button><UnlinkedBadge tenant={d} forReturn={true} /></td>
                          <td className={tdClass}>{d.property_name} / Unit {d.unit_number}</td>
                          <td className={`${tdClass} tabular-nums`}>{fmtDate(d.move_in_date)}</td>
                          <td className={`${tdClass} tabular-nums`}>{fmtDate(d.move_out_date)}</td>
                          <td className={`${tdClass} tabular-nums`}>{fmtMoney(d.deposit_amount)}</td>
                          <td className={`${tdClass} tabular-nums`}>{fmtDate(d.deposit_return_date)}</td>
                          <td className={`${tdClass} tabular-nums`}>{fmtMoney(d.deposit_return_amount)}</td>
                          <td className={tdClass}>{d.deposit_return_method || '-'}</td>
                          <td className={tdClass}><Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEditDialog(d)} data-testid="edit-past-deposit-btn"><Pencil className="h-3 w-3" /></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    );
  };

  const renderLandlordTab = () => (
    <div>
      <Card className="mb-4 bg-amber-50 border-amber-200">
        <CardContent className="py-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Total Landlord Deposits</p>
          <p className="font-heading text-3xl font-bold tabular-nums text-amber-900" data-testid="landlord-deposits-total">{fmtMoney(landlordData.total)}</p>
        </CardContent>
      </Card>
      <div className="space-y-2">
        {landlordData.properties?.map(prop => {
          const isExpanded = expandedLandlordProps[prop.property_id];
          return (
            <Card key={prop.property_id} className="overflow-hidden">
              <button className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
                onClick={() => setExpandedLandlordProps(p => ({ ...p, [prop.property_id]: !p[prop.property_id] }))}>
                <div className="flex items-center gap-2">
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <span className="text-sm font-semibold">{prop.property_name}</span>
                  {prop.building_id != null && <Badge variant="outline" className="text-xs">Bldg #{prop.building_id}</Badge>}
                </div>
                <span className="text-sm font-semibold tabular-nums">{fmtMoney(prop.total)}</span>
              </button>
              {isExpanded && (
                <div className="border-t">
                  {prop.units.map((u, idx) => (
                    <div key={u.unit_id} className={`flex items-center justify-between px-6 py-3 ${idx % 2 === 0 ? 'bg-white' : 'bg-muted/20'} border-b border-border/30`}>
                      <span className="text-sm">Unit {u.unit_number}</span>
                      {editingLandlord === u.unit_id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm">$</span>
                          <Input className="w-28 h-8 text-sm" type="number" value={landlordAmount} onChange={e => setLandlordAmount(e.target.value)} />
                          <Button size="sm" className="h-7 w-7 p-0" onClick={() => handleSaveLandlordDeposit(u.unit_id)}><Check className="h-3 w-3" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditingLandlord(null)}><X className="h-3 w-3" /></Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-sm tabular-nums font-medium">{u.landlord_deposit ? fmtMoney(u.landlord_deposit) : '-'}</span>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditingLandlord(u.unit_id); setLandlordAmount(u.landlord_deposit || ''); }}><Pencil className="h-3 w-3" /></Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );

  const renderPnlTab = () => {
    const matchedCount = pnlTxns.filter(t => reconciliation[t.id]).length;
    const unmatchedCount = pnlTxns.length - matchedCount;

    return (
    <div className="space-y-3" data-testid="pnl-deposits-tab-content">
      {/* Summary card */}
      <Card className="border-border/60">
        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Security-Deposit Transactions (from Bank P&L)</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Matched against your Current + Past tenant deposits by amount and date (±14 days)</p>
          </div>
          <div className="flex gap-5">
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Transactions</p>
              <p className="text-lg font-semibold tabular-nums" data-testid="pnl-deposits-count">{pnlTxns.length}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Matched</p>
              <p className="text-lg font-semibold tabular-nums text-emerald-700" data-testid="pnl-deposits-matched">{matchedCount}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Unmatched</p>
              <p className={`text-lg font-semibold tabular-nums ${unmatchedCount > 0 ? 'text-amber-600' : 'text-muted-foreground'}`} data-testid="pnl-deposits-unmatched">{unmatchedCount}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Net</p>
              <p className={`text-lg font-semibold tabular-nums ${pnlTotal >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="pnl-deposits-net">
                {pnlTotal >= 0 ? '+' : ''}{fmtMoney(pnlTotal)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transactions table */}
      {pnlTxns.length === 0 ? (
        <div className="rounded-lg border border-border/60 bg-white p-12 text-center text-sm text-muted-foreground" data-testid="pnl-deposits-empty">
          <p className="font-medium">No security deposit transactions yet</p>
          <p className="text-xs mt-1">Categorize bank transactions as <span className="font-semibold">Income › Security Deposit Received</span> or <span className="font-semibold">Transfers & Internal › Security Deposit Returned</span> — they will show here.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-border/60 bg-white overflow-hidden">
          <table className="w-full text-xs" data-testid="pnl-deposits-table">
            <thead>
              <tr className="bg-[#f5f0eb] text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground border-b border-border/50">
                <th className="px-3 py-2 w-24">Date</th>
                <th className="px-3 py-2">Title / Description</th>
                <th className="px-3 py-2 w-40">Category</th>
                <th className="px-3 py-2 w-48">Reconcile with Deposits</th>
                <th className="px-3 py-2 text-right w-28">Amount</th>
              </tr>
            </thead>
            <tbody>
              {pnlTxns.map((txn, idx) => {
                const match = reconciliation[txn.id];
                return (
                  <tr
                    key={txn.id}
                    onClick={() => { setPnlEditIndex(idx); setPnlEditOpen(true); }}
                    className={`${idx % 2 === 0 ? 'bg-white' : 'bg-[#fafaf7]'} hover:bg-[#f0ece6] transition-colors border-b border-border/30 last:border-b-0 cursor-pointer`}
                    data-testid="pnl-deposit-row"
                  >
                    <td className="px-3 py-2 whitespace-nowrap tabular-nums">{fmtDate(txn.date)}</td>
                    <td className="px-3 py-2 max-w-[320px]">
                      {txn.title ? (
                        <span className="font-medium truncate block">{txn.title}</span>
                      ) : (
                        <span className="italic text-muted-foreground truncate block">{txn.description || '—'}</span>
                      )}
                      {txn.title && txn.description && (
                        <span className="text-[10px] text-muted-foreground truncate block mt-0.5">{txn.description}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="secondary" className="text-[10px] font-normal">
                        {txn.subcategory || txn.category || '—'}
                        {txn.detail ? ` › ${txn.detail}` : ''}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      {match ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); setTenantDetailId(match.deposit.id); }}
                            className="inline-flex flex-col text-left hover:underline flex-1 min-w-0"
                            title={`View ${match.deposit.name}`}
                            data-testid={match.manual ? 'reconcile-manual' : 'reconcile-matched'}
                          >
                            <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${match.manual ? 'text-sky-700' : 'text-emerald-700'}`}>
                              {match.manual ? <Link2 className="h-3 w-3" /> : <Check className="h-3 w-3" />}
                              <span className="truncate">{match.deposit.name}</span>
                            </span>
                            <span className="text-[10px] text-muted-foreground truncate">
                              {match.deposit.unit_number ? `Unit ${match.deposit.unit_number}` : match.deposit.property_name}
                              {match.manual ? ' · manual link' : (match.days > 0 ? ` · ${Math.round(match.days)}d ${match.matchType === 'return' ? 'from return' : 'from move-in'}` : ' · exact match')}
                            </span>
                          </button>
                          {/* Edit — allow changing even exact matches (mistakes happen) */}
                          <button
                            onClick={(e) => { e.stopPropagation(); setLinkDialogTxn(txn); setLinkSearch(''); }}
                            className="flex-shrink-0 h-6 w-6 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-sky-700 hover:bg-sky-50 transition-colors"
                            title={match.manual ? 'Change linked tenant' : 'Override this match'}
                            data-testid="edit-link-btn"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); setLinkDialogTxn(txn); setLinkSearch(''); }}
                          className="inline-flex items-center gap-1 text-[11px] text-amber-700 hover:text-sky-700 hover:underline"
                          data-testid="link-tenant-btn"
                        >
                          <Link2 className="h-3 w-3" />
                          {txn.manual_unlink ? 'Marked unmatched — re-link' : 'Link to tenant…'}
                        </button>
                      )}
                    </td>
                    <td className={`px-3 py-2 whitespace-nowrap font-semibold tabular-nums text-right ${txn.type === 'income' ? 'text-emerald-700' : 'text-red-700'}`}>
                      <span className="inline-flex items-center gap-1 justify-end">
                        {txn.type === 'income' ? <ArrowUpCircle className="h-3 w-3 text-emerald-500" /> : <ArrowDownCircle className="h-3 w-3 text-red-400" />}
                        {txn.type === 'income' ? '+' : '-'}{fmtMoney(Math.abs(txn.amount || 0))}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
    );
  };


  return (
    <div className="space-y-6" data-testid="deposits-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Deposits</h1>
        <p className="text-sm text-muted-foreground mt-1">Track tenant and landlord deposits</p>
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-40 text-muted-foreground">Loading...</div>
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="deposits-tabs">
            <TabsTrigger value="current" data-testid="tab-current-deposits">
              Current Deposits <Badge variant="secondary" className="ml-2 text-xs">{currentData.deposits.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="past" data-testid="tab-past-deposits">
              Past Deposits <Badge variant="secondary" className="ml-2 text-xs">{pastData.deposits?.length || 0}</Badge>
            </TabsTrigger>
            <TabsTrigger value="landlord" data-testid="tab-landlord-deposits">Landlord Deposits</TabsTrigger>
            <TabsTrigger value="pnl" data-testid="tab-pnl-deposits">
              Deposits from P&L <Badge variant="secondary" className="ml-2 text-xs">{pnlTxns.length}</Badge>
            </TabsTrigger>
          </TabsList>
          <TabsContent value="current" className="mt-4">{renderCurrentTab()}</TabsContent>
          <TabsContent value="past" className="mt-4">{renderPastTab()}</TabsContent>
          <TabsContent value="landlord" className="mt-4">{renderLandlordTab()}</TabsContent>
          <TabsContent value="pnl" className="mt-4">{renderPnlTab()}</TabsContent>
        </Tabs>
      )}

      <ReturnDepositDialog
        open={!!returnDialog} onClose={() => { setReturnDialog(null); setConfirmReturn(false); }}
        deposit={returnDialog} returnForm={returnForm} setReturnForm={setReturnForm}
        confirmReturn={confirmReturn} onNext={handleReturnDeposit} saving={saving} fmtMoney={fmtMoney}
      />
      <EditDepositDialog
        open={!!editDialog} onClose={() => setEditDialog(null)}
        deposit={editDialog} editForm={editForm} setEditForm={setEditForm}
        onSave={handleSaveEdit} saving={saving}
      />
      <TenantDetailModal tenantId={tenantDetailId} open={!!tenantDetailId} onClose={() => setTenantDetailId(null)} />

      {/* P&L Deposit Transaction Edit Dialog */}
      <TransactionEditDialog
        open={pnlEditOpen}
        onOpenChange={(v) => { setPnlEditOpen(v); if (!v) setPnlEditIndex(-1); }}
        transaction={pnlEditTarget}
        categories={pnlCategories}
        onSaved={() => fetchPnlDeposits()}
        onPrev={() => setPnlEditIndex(i => Math.max(0, i - 1))}
        onNext={() => setPnlEditIndex(i => Math.min(pnlTxns.length - 1, i + 1))}
        hasPrev={pnlEditIndex > 0}
        hasNext={pnlEditIndex >= 0 && pnlEditIndex < pnlTxns.length - 1}
        position={pnlEditIndex >= 0 ? { current: pnlEditIndex + 1, total: pnlTxns.length } : null}
      />

      {/* Link transaction to tenant dialog */}
      <Dialog open={!!linkDialogTxn} onOpenChange={(v) => { if (!v) { setLinkDialogTxn(null); setLinkSearch(''); } }}>
        <DialogContent className="max-w-lg max-h-[75vh] overflow-hidden flex flex-col" data-testid="link-tenant-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[15px]">
              <Link2 className="h-4 w-4 text-sky-700" />
              Link transaction to tenant
            </DialogTitle>
            <DialogDescription className="text-[12px]">
              {linkDialogTxn && (
                <span>
                  {fmtDate(linkDialogTxn.date)} · {linkDialogTxn.title || linkDialogTxn.description || '—'} ·
                  <span className={`font-semibold ml-1 ${(linkDialogTxn.amount || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {(linkDialogTxn.amount || 0) >= 0 ? '+' : '-'}{fmtMoney(Math.abs(linkDialogTxn.amount || 0))}
                  </span>
                </span>
              )}
              <span className="block mt-1 text-muted-foreground">Multiple transactions can link to the same tenant (e.g. split deposits).</span>
            </DialogDescription>
          </DialogHeader>
          <div className="relative my-2">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search tenant by name, unit, or property…"
              className="pl-8 h-9 text-xs"
              value={linkSearch}
              onChange={(e) => setLinkSearch(e.target.value)}
              autoFocus
              data-testid="link-tenant-search"
            />
          </div>
          {/* Current match status + override actions */}
          {linkDialogTxn && (() => {
            const current = reconciliation[linkDialogTxn.id];
            if (!current) return null;
            return (
              <div className={`flex items-center justify-between gap-2 mb-2 px-3 py-2 rounded-md border text-[11px] ${current.manual ? 'border-sky-200 bg-sky-50/60' : 'border-emerald-200 bg-emerald-50/50'}`} data-testid="current-match-strip">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#1f2933] truncate flex items-center gap-1">
                    {current.manual ? <Link2 className="h-3 w-3 text-sky-700" /> : <Check className="h-3 w-3 text-emerald-700" />}
                    {current.manual ? 'Currently linked to ' : 'Auto-matched to '}
                    <span className="underline">{current.deposit.name}</span>
                  </div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    Pick a new tenant below to override, or:
                  </div>
                </div>
                {current.manual ? (
                  <Button
                    size="sm" variant="outline" className="h-7 text-[11px] gap-1"
                    onClick={() => handleLinkTenant(linkDialogTxn, null, false)}
                    disabled={linkSaving}
                    data-testid="unlink-tenant-btn"
                  >
                    <Unlink className="h-3 w-3" />Unlink
                  </Button>
                ) : (
                  <Button
                    size="sm" variant="outline" className="h-7 text-[11px] gap-1"
                    onClick={() => handleLinkTenant(linkDialogTxn, null, true)}
                    disabled={linkSaving}
                    data-testid="reject-auto-match-btn"
                  >
                    <AlertCircle className="h-3 w-3" />Not this tenant
                  </Button>
                )}
              </div>
            );
          })()}
          {/* If previously rejected, show restore-auto-match option */}
          {linkDialogTxn?.manual_unlink && !reconciliation[linkDialogTxn.id] && (
            <div className="flex items-center justify-between gap-2 mb-2 px-3 py-2 rounded-md border border-amber-200 bg-amber-50/60 text-[11px]" data-testid="rejected-match-strip">
              <span className="text-muted-foreground">You rejected the auto-match for this transaction.</span>
              <Button
                size="sm" variant="outline" className="h-7 text-[11px]"
                onClick={() => handleLinkTenant(linkDialogTxn, null, false)}
                disabled={linkSaving}
                data-testid="restore-auto-match-btn"
              >
                Re-enable auto-match
              </Button>
            </div>
          )}
          <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-1">
            {(() => {
              const q = linkSearch.toLowerCase();
              const matches = allTenants
                .filter(t => {
                  if (!q) return true;
                  const full = `${t.first_name || ''} ${t.last_name || ''}`.toLowerCase();
                  return full.includes(q)
                    || (t.name || '').toLowerCase().includes(q)
                    || (t.unit_number || '').toString().toLowerCase().includes(q)
                    || (t.property_name || '').toLowerCase().includes(q)
                    || (t.email || '').toLowerCase().includes(q);
                })
                .slice(0, 80);
              if (matches.length === 0) {
                return <div className="py-6 text-center text-xs text-muted-foreground">No tenants found</div>;
              }
              return matches.map(t => {
                const name = t.name || `${t.first_name || ''} ${t.last_name || ''}`.trim() || '(no name)';
                const linkedCount = linkedCountByTenant[t.id] || 0;
                const status = getTenantStatus(t);
                const statusColor = status === 'current' ? 'text-emerald-700' : status === 'future' ? 'text-sky-700' : 'text-muted-foreground';
                return (
                  <button
                    key={t.id}
                    onClick={() => handleLinkTenant(linkDialogTxn, t.id)}
                    disabled={linkSaving}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-border/50 bg-white hover:bg-[#faf8f4] transition-colors text-left disabled:opacity-50"
                    data-testid="link-tenant-option"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-[#1f2933] truncate">{name}</div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {t.unit_number ? `Unit ${t.unit_number}` : ''}{t.property_name ? ` · ${t.property_name}` : ''}
                        {t.move_in_date ? ` · moved in ${fmtDate(t.move_in_date)}` : ''}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                      <span className={`text-[9px] uppercase tracking-wide font-semibold ${statusColor}`}>{status}</span>
                      {linkedCount > 0 && (
                        <Badge variant="secondary" className="text-[9px] h-4 px-1.5 font-normal">{linkedCount} linked</Badge>
                      )}
                    </div>
                  </button>
                );
              });
            })()}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
